import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { params } from "../../vars/params";
import { config } from "../../../config";

const LOGO_MASK_TARGET = "#logo-mask path, #logo-mask line, #logo-mask polyline, #logo-mask polygon";
const LOGO_MASK_TARGET_2 = "#logo-mask2 path, #logo-mask2 line, #logo-mask2 polyline, #logo-mask2 polygon";
const LOG_PREFIX = "[logo-mask]";
const LOG_PREFIX_2 = "[logo-mask2]";

gsap.registerPlugin(ScrollTrigger, SplitText);

const calculateLength = (element: SVGGeometryElement): number => {
  if (typeof element.getTotalLength === "function") {
    return element.getTotalLength();
  }

  if (element instanceof SVGCircleElement) {
    const radius = element.r.baseVal.value;
    return 2 * Math.PI * radius;
  }

  if (element instanceof SVGEllipseElement) {
    const radiusX = element.rx.baseVal.value;
    const radiusY = element.ry.baseVal.value;
    return Math.PI * (3 * (radiusX + radiusY) - Math.sqrt((3 * radiusX + radiusY) * (radiusX + 3 * radiusY)));
  }

  if (element instanceof SVGLineElement) {
    const { x1, y1, x2, y2 } = element;
    const dx = x2.baseVal.value - x1.baseVal.value;
    const dy = y2.baseVal.value - y1.baseVal.value;
    return Math.sqrt(dx * dx + dy * dy);
  }

  console.warn(LOG_PREFIX, "未対応の要素で長さを取得できませんでした", element);
  return 0;
};

export const mv = (): void => {
  const logo = document.getElementById("logo");
  const offsets = {
    "mask-1-1": 0,
    "mask-2-1": 0,
    "mask-2-2": 0,
    "mask-3-1": 0,
  };

  if (!logo) {
    console.warn(LOG_PREFIX, "#logo が見つかりません");
    return;
  }

  const maskNodes = logo.querySelectorAll<SVGGeometryElement>(LOGO_MASK_TARGET);
  const targets = Array.from(maskNodes);

  console.debug(LOG_PREFIX, "取得した要素数", targets.length, targets);

  if (!targets.length) {
    console.warn(LOG_PREFIX, "マスク要素が取得できませんでした");
    return;
  }

  targets.forEach((target) => {
    const length = calculateLength(target);
    console.debug(LOG_PREFIX, "要素の長さ", { tag: target.tagName, id: target.id, length });

    if (!length) {
      console.warn(LOG_PREFIX, "長さが 0 のため dasharray を設定しません", target);
      return;
    }

    offsets[target.id] = length;

    gsap.set(target, {
      strokeDasharray: length,
      strokeDashoffset: length,
    });

    console.debug(LOG_PREFIX, "strokeDash 設定完了", {
      tag: target.tagName,
      id: target.id,
      dasharray: length,
      dashoffset: length,
    });
  });
  // console.log(offsets["mask-1-1"]);
  const logoTl = gsap.timeline({
    delay: 0.6,
  });
  logoTl.to(
    "#mask-1-1",
    {
      strokeDashoffset: 0,
      duration: 4,
      ease: "power2.out",
    },
    0
  );
  logoTl.to(
    "#mask-2-1",
    {
      strokeDashoffset: 0,
      duration: 3.5,
      ease: "power2.out",
    },
    0.5
  );
  logoTl.to(
    "#mask-2-2",
    {
      strokeDashoffset: 0,
      duration: 2,
      ease: "power2.out",
    },
    1.2
  );
  logoTl.to(
    "#mask-3-1",
    {
      strokeDashoffset: 0,
      duration: 3,
      ease: "power2.out",
    },
    1
  );
  logoTl.fromTo(
    ".logo-type",
    {
      opacity: 0,
      y: params.bH * -0.05,
    },
    {
      opacity: 1,
      y: 0,
      duration: 2,
      stagger: 0.2,
      ease: "power2.out",
    },
    1.8
  );

  let mouseAngle = 0;
  let opacityScrollValue = 1;
  let lastPointerPosition = {
    clientX: window.innerWidth / 2 + 400,
    clientY: window.innerHeight / 2 - 400,
  };

  const updateDropShadow = (e: MouseEvent | { clientX: number; clientY: number }): void => {
    const x = e.clientX ?? lastPointerPosition.clientX;
    const y = e.clientY ?? lastPointerPosition.clientY;
    const dx = x - window.innerWidth / 2;
    const dy = y - window.innerHeight / 2;
    const angle = Math.atan2(dy, dx);
    mouseAngle = angle;

    const distance = Math.sqrt(dx * dx + dy * dy) || 1;
    const intensity = Math.min(distance / 800, 0.35);
    const brightX = Math.cos(mouseAngle) * intensity;
    const brightY = Math.sin(mouseAngle) * intensity;
    const darkX = -brightX;
    const darkY = -brightY;

    // console.log(LOG_PREFIX, "mouse", { x, y, mouseAngle, intensity });

    const filterValue = `drop-shadow(${brightX}rem ${brightY}rem 0.125rem rgba(255, 255, 255, ${0.75 * opacityScrollValue})) drop-shadow(${darkX}rem ${darkY}rem 0.125rem rgba(168, 168, 168, ${0.24 * opacityScrollValue}))`;

    gsap.to(".p-top-mv__inner", {
      filter: filterValue,
      duration: 1.5,
      ease: "power2.out",
    });
    // console.log(LOG_PREFIX, "filter", filterValue);
  };

  updateDropShadow(lastPointerPosition);
  window.addEventListener("mousemove", (e) => {
    lastPointerPosition = {
      clientX: e.clientX,
      clientY: e.clientY,
    };
    updateDropShadow(e);
  });
  updateDropShadow(lastPointerPosition);
  window.addEventListener("touchmove", () => {
    lastPointerPosition = {
      clientX: window.innerWidth / 2 + 400,
      clientY: window.innerHeight / 2 - 400,
    };
    updateDropShadow(lastPointerPosition);
  });
  window.addEventListener("touchstart", () => {
    lastPointerPosition = {
      clientX: window.innerWidth / 2 + 400,
      clientY: window.innerHeight / 2 - 400,
    };
    updateDropShadow(lastPointerPosition);
  });

  window.addEventListener("scroll", () => {
    opacityScrollValue = Math.min((params.bH * 0.7 - window.scrollY) / (params.bH * 0.7), 1);
    updateDropShadow(lastPointerPosition);
  });

  gsap.to(".p-top-mv__bg", {
    opacity: 1,
    duration: 2,
    ease: "none",
    scrollTrigger: {
      trigger: "body",
      start: "top -5%",
      end: `+=${params.bH * 0.65}`,
      scrub: true,
    },
  });

  const logoWidth = logo.clientWidth;
  const logoHeight = logo.clientHeight;
  const logoDistX = (0.8 * (params.bW - logoWidth)) / 2;
  const logoDistY = (0.1 * (params.bH - logoHeight)) / 2;
  // console.log(LOG_PREFIX, "logoDist", { logoWidth, logoHeight, logoDistX, logoDistY });
  const logoTl2 = gsap.timeline({
    scrollTrigger: {
      trigger: "body",
      start: "top -5%",
      end: `+=${params.bH * 0.65}`,
      scrub: true,
    },
  });
  logoTl2.to(
    ".p-top-mv__logo",
    {
      // rotation: 360,
      // duration: 10,
      // x: logoDistX,
      // y: -logoDistY,
      duration: 4,
      ease: "none",
      filter: "blur(2px)",
      opacity: 0,
      // scale: 1.783,
      // onComplete: () => {
      //   gsap.set(".p-top-mv__logo", {
      //     willChange: "transform",
      //     backfaceVisibility: "hidden",
      //   });
      // },
      // onReverseComplete: () => {
      //   gsap.set(".p-top-mv__logo", {
      //     willChange: "unset",
      //     backfaceVisibility: "hidden",
      //   });
      // },
    },
    0
  );
  logoTl2.to(
    "#logo-type",
    {
      duration: 4,
      ease: "none",
      y: -logoDistY,
      opacity: 0,
      scale: 1.1,
    },
    2
  );
  // About group text animations
  gsap.utils.toArray(".p-top-concept__desc").forEach((group) => {
    const groupElement = group as Element;
    const splitText = new SplitText(groupElement, { types: "chars", tag: "span" });
    gsap.from(splitText.chars, {
      opacity: 0.3,
      duration: 0.7,
      stagger: 0.1,
      scrollTrigger: {
        trigger: groupElement,
        start: "top 70%",
        end: "bottom 60%",
        scrub: true,
      },
    });
  });
  gsap.to("#logo", {
    opacity: 0,
    rotation: 540,
    x: `${params.bW * 0.3}px`,
    y: `${params.bH * -0.1}px`,
    duration: 2,
    ease: "none",
    scrollTrigger: {
      trigger: ".p-top-concept__desc:last-child",
      start: "bottom 50%",
      end: `bottom 0%`,
      scrub: true,
    },
  });
  const slideBgSize = {
    width: config.breakPoint <= params.bW ? (params.bW * 397) / 1920 : (params.bW * 281) / 750,
    height: config.breakPoint <= params.bW ? (params.bW * 722) / 1920 : (params.bW * 512) / 750,
    borderRadius: config.breakPoint <= params.bW ? (params.bW * 198) / 1920 : (params.bW * 141) / 750,
  };
  gsap.to(".p-top-mv__bg", {
    width: slideBgSize.width + "px",
    height: slideBgSize.height + "px",
    borderRadius: slideBgSize.borderRadius + "px",
    duration: 2,
    ease: "none",
    scrollTrigger: {
      trigger: ".p-top-concept__desc:last-child",
      start: "bottom 50%",
      end: `bottom 0%`,
      scrub: true,
    },
  });
  const logoType2Tl = gsap.timeline({
    scrollTrigger: {
      trigger: ".p-top-concept__desc:last-child",
      start: "bottom 20%",
      end: `bottom -130%`,
      scrub: true,
    },
  });
  logoType2Tl.to(
    ".p-top-mv__logo-type-2",
    {
      opacity: 1,
      duration: 1,
      ease: "power2.out",
    },
    0
  );
  logoType2Tl.fromTo(
    ".logo-type-2__path",
    {
      opacity: 0,
      y: params.bH * 0.15,
    },
    {
      opacity: 1,
      y: 0,
      duration: 2,
      stagger: 0.2,
      ease: "power2.out",
    }
  );
  logoType2Tl.fromTo(
    ".logo-type-2__path",
    {
      opacity: 1,
      y: 0,
    },
    {
      opacity: 0,
      y: params.bH * -0.15,
      delay: 3,
      duration: 6,
      stagger: 0.2,
      ease: "power2.out",
    }
  );
  gsap.to(".p-top-mv__slide", {
    x: `-=${105 * 2.5}%`,
    duration: 2,
    ease: "none",
    scrollTrigger: {
      trigger: ".p-top-concept__desc:last-child",
      start: "bottom -30%",
      end: `bottom -415%`,
      scrub: true,
    },
  });
  // gsap.to(".p-top-mv__slide", {
  //   y: `-=${params.bH}px`,
  //   duration: 2,
  //   ease: "none",
  //   scrollTrigger: {
  //     trigger: ".p-top-concept__desc",
  //     start: "bottom -315%",
  //     end: `bottom -415%`,
  //     scrub: true,
  //   },
  // });
  const slide0Tl = gsap.timeline();
  slide0Tl.to(".p-top-mv__slide.--original", {
    y: `-=30%`,
    ease: "none",
    scrollTrigger: {
      trigger: ".p-top-concept__desc:last-child",
      start: "bottom -245%",
      end: `bottom -295%`,
      scrub: true,
    },
  });
  slide0Tl.to(".p-top-mv__slide.--original video", {
    opacity: 0,
    ease: "none",
    scrollTrigger: {
      trigger: ".p-top-concept__desc:last-child",
      start: "bottom -245%",
      end: `bottom -295%`,
      scrub: true,
    },
  });
  const slide1Tl = gsap.timeline();
  slide1Tl.fromTo(
    ".p-top-mv__slide.--more-1",
    {
      opacity: 0,
      y: `-20%`,
    },
    {
      opacity: 1,
      y: `-50%`,
      duration: 2,
      ease: "none",
      scrollTrigger: {
        trigger: ".p-top-concept__desc:last-child",
        start: "bottom -90%",
        end: `bottom -140%`,
        scrub: true,
      },
    }
  );
  slide1Tl.fromTo(
    ".p-top-mv__slide.--more-1",
    { y: `-50%` },
    {
      y: `-80%`,
      ease: "none",
      scrollTrigger: {
        trigger: ".p-top-concept__desc:last-child",
        start: "bottom -285%",
        end: `bottom -335%`,
        scrub: true,
      },
    }
  );
  slide1Tl.to(".p-top-mv__slide.--more-1 img", {
    opacity: 0,
    ease: "none",
    scrollTrigger: {
      trigger: ".p-top-concept__desc:last-child",
      start: "bottom -285%",
      end: `bottom -335%`,
      scrub: true,
    },
  });
  const slide2Tl = gsap.timeline();
  slide2Tl.fromTo(
    ".p-top-mv__slide.--more-2",
    {
      opacity: 0,
      y: `-20%`,
    },
    {
      opacity: 1,
      y: `-50%`,
      duration: 2,
      ease: "none",
      scrollTrigger: {
        trigger: ".p-top-concept__desc:last-child",
        start: "bottom -130%",
        end: `bottom -180%`,
        scrub: true,
      },
    }
  );
  slide2Tl.fromTo(
    ".p-top-mv__slide.--more-2",
    { y: `-50%` },
    {
      y: `-80%`,
      ease: "none",
      scrollTrigger: {
        trigger: ".p-top-concept__desc:last-child",
        start: "bottom -325%",
        end: `bottom -375%`,
        scrub: true,
      },
    }
  );
  slide2Tl.to(".p-top-mv__slide.--more-2 img", {
    opacity: 0,
    ease: "none",
    scrollTrigger: {
      trigger: ".p-top-concept__desc:last-child",
      start: "bottom -325%",
      end: `bottom -375%`,
      scrub: true,
    },
  });
  const slide3Tl = gsap.timeline();
  slide3Tl.fromTo(
    ".p-top-mv__slide.--more-3",
    {
      opacity: 0,
      y: `-20%`,
    },
    {
      opacity: 1,
      y: `-50%`,
      duration: 2,
      ease: "none",
      scrollTrigger: {
        trigger: ".p-top-concept__desc:last-child",
        start: "bottom -170%",
        end: `bottom -220%`,
        scrub: true,
      },
    }
  );
  slide3Tl.fromTo(
    ".p-top-mv__slide.--more-3",
    { y: `-50%` },
    {
      y: `-80%`,
      ease: "none",
      scrollTrigger: {
        trigger: ".p-top-concept__desc:last-child",
        start: "bottom -365%",
        end: `bottom -415%`,
        scrub: true,
      },
    }
  );
  slide3Tl.to(".p-top-mv__slide.--more-3 img", {
    opacity: 0,
    ease: "none",
    scrollTrigger: {
      trigger: ".p-top-concept__desc:last-child",
      start: "bottom -365%",
      end: `bottom -415%`,
      scrub: true,
    },
  });
  const logo2Tl = gsap.timeline({
    scrollTrigger: {
      trigger: ".p-top-concept__desc:first-child",
      endTrigger: ".p-top-concept__desc:last-child",
      start: "top 100%",
      end: `bottom 100%`,
      scrub: true,
    },
  });
  logo2Tl.to(".p-top-mv__logo-2", {
    opacity: 0.78,
    duration: 3,
    ease: "none",
  });
  logo2Tl.to(
    ".p-top-mv__logo-2",
    {
      y: `+=${params.bH * 0.02}px`,
      duration: 10,
      ease: "none",
    },
    0
  );
  logo2Tl.to(
    ".p-top-mv__logo-2",
    {
      opacity: 0,
      duration: 3,
      ease: "none",
    },
    7
  );
};
